import styles from '@cp/pages/CaseDetails/components/PhoneNumber/index.module.scss';
import { DateFormat, getFormatDate, utcToIST } from '@cp/utils/DateHelper';
import { POC_TAG_SOURCES } from '@cp/pages/CaseDetails/components/PocTag/constants';
import cx from 'classnames';
import CircularProgress from '@cp/components/ProgressBars/circularProgress/CircularProgress';
import Typography from '@primitives/Typography';
import CallHistory from '@cp/components/CallHistory/CallHistory';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/components/TooltipV2/TooltipV2';
import { PhoneNumberSourceText } from '@cp/components/Ameyo/interfaces';
import React, { useMemo } from 'react';
import {
  IPhoneNumberLimit,
  ITelePhoneData,
  LIMIT_TYPE
} from '@cp/pages/CaseDetails/interfaces/CaseDetail.type';
import PocNumbersTag from '../PocTag/Index';
import dayjs from 'dayjs';
import PersonWithSeperatorIcon from '@cp/src/assets/icons/PersonWithSeprator';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { Roles } from '@cp/src/pages/auth/constants/AuthConstants';
import { Popper, PopperContent, PopperTrigger } from '@cp/src/components/Popper/Popper';
import Button from '@navi/web-ui/lib/primitives/Button/Button';
import VerticalSeperator from '@cp/src/components/VerticalSeperator';
import DeleteIcon from '@cp/src/assets/icons/DeleteIcon';
import RemoveNumberModal from './RemoveNumberModal';
import { LIMIT_CALLS } from '@cp/src/constants/Common.constants';
import InfoSolidSmallIcon from '@cp/src/assets/icons/InfoSolidSmallIcon';
import GlobalRoleAndNoFromLonghorn from './GlobalRoleAndNoFromLonghorn';

const NO_OF_RECORDS = 5;

const PhoneNumberContactCard = ({ callData }: { callData: ITelePhoneData }) => {
  const userRole = useSelector((state: RootState) => state?.common?.userData?.roles);
  const { dailyLimit, hourlyLimit, callRemainingPercentage } = useMemo(() => {
    const dailyLimit = callData?.limit?.[LIMIT_TYPE.DAILY] as IPhoneNumberLimit | undefined;
    const hourlyLimit = callData?.limit?.[LIMIT_TYPE.HOURLY] as IPhoneNumberLimit | undefined;
    return {
      dailyLimit,
      hourlyLimit,
      callRemainingPercentage: dailyLimit
        ? Math.floor((dailyLimit?.callRemaining / dailyLimit?.maxCalls) * 100)
        : 0
    };
  }, [callData]);

  const isAddedFromLonghorn = callData?.showCreatedBy || false;

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const isPrimary = callData?.sources.includes('Primary');
  const [isHovered, setIsHovered] = React.useState(false);

  const isGlobalRoleAndNnumberformLonghorn =
    userRole?.includes(Roles.ROLE_GLOBAL_ACCESS) && isAddedFromLonghorn;

  return callData?.number ? (
    <div className={styles.callHistoryCard} key={callData?.number}>
      <div className={styles.dateWrapper}>
        <div className={styles.dateText}>
          {getFormatDate(utcToIST(callData?.createdAt as unknown as Date), DateFormat.DD_MMM_YYYY)}
        </div>
        {userRole?.includes(Roles.ROLE_GLOBAL_ACCESS) && !isPrimary && (
          <div className="flex flex-row items-center gap-[4px] pr-[8px]">
            <VerticalSeperator />
            <div
              onMouseEnter={() => setIsHovered(!isHovered)}
              onMouseLeave={() => setIsHovered(!isHovered)}
              className="pl-[6px]"
            >
              <Button variant="text" onClick={() => setIsModalOpen(true)}>
                <DeleteIcon
                  fillColor={isHovered ? 'var(--red-base)' : 'var(--grayscale-3)'}
                  className={styles.deleteIcon}
                />
              </Button>
            </div>
            <RemoveNumberModal
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              callData={callData}
            />
          </div>
        )}
      </div>

      <div className={cx(styles.phoneNumberWrapperStyles)}>
        {callData?.isPoc ? (
          <PocNumbersTag source={POC_TAG_SOURCES.CONTACT_TAB} number={callData?.number} />
        ) : (
          <div className={cx(styles.phoneNumber, 'rr-block')}> {callData?.number} </div>
        )}
        {callData?.new && <div className={styles.newNumberTag}>New</div>}
        {dailyLimit?.remarks ? (
          <div className="ml-2">
            <Popper placement="bottom-end">
              <PopperTrigger>
                <Button
                  className={styles.moreInfoButton}
                  variant={'text'}
                  startAdornment={<InfoSolidSmallIcon fill="var( --grayscale-3)" />}
                >
                  Multiple LANs
                </Button>
              </PopperTrigger>
              <PopperContent
                noHeader={true}
                className={cx('popperWrapper', 'p-0')}
                arrowColor="var(--orange-oldlase)"
              >
                <div className={styles.popperContent}>
                  <div className="flex flex-row ml-4 my-4">
                    <Typography variant="p4" color="var(--grayscale-3)" as="span">
                      <Typography variant="h5" color="var(--grayscale-2)" as="span">
                        Multiple LANs{' '}
                      </Typography>{' '}
                      are linked to this <br />
                      number
                    </Typography>
                  </div>
                </div>
              </PopperContent>
            </Popper>
          </div>
        ) : null}
      </div>

      <div className={styles.callHistoryTruecallerWrapper}>
        <div className={cx(styles.limitsContainer, 'ml-4 mb-4')}>
          <div className={styles.limitsProgress}>
            <CircularProgress
              sqSize={20}
              percentage={
                callRemainingPercentage > 90 && callRemainingPercentage < 100
                  ? 90
                  : callRemainingPercentage
              }
              strokeWidth={3}
              withAnimation
              circleProgress={hourlyLimit?.callRemaining === 0 ? styles.circleProgress : ''}
            />
            <Typography className="flex items-center" variant="h5" color="var(--grayscale-2)">
              <Typography
                variant="h5"
                as="span"
                color={dailyLimit?.callRemaining === 0 ? 'var(--red-base)' : 'var(--grayscale-2)'}
                className="mr-[2px]"
              >
                {dailyLimit?.callRemaining}
              </Typography>
              calls remaining
            </Typography>
          </div>
          {dailyLimit?.callRemaining === 0 ? (
            <div className={styles.limitswrapper}>
              <Typography variant="h5" color="var(--grayscale-2)">
                {LIMIT_CALLS.DAILY_LIMIT_REACHED}
                <Typography
                  as="span"
                  variant="h5"
                  className="font-medium"
                  color="var(--orange-darkbase)"
                >
                  {LIMIT_CALLS.TOMORROW}
                </Typography>
              </Typography>
            </div>
          ) : hourlyLimit?.nextCallingTime ? (
            <div className={styles.limitswrapper}>
              <Typography variant="h5" color="var(--grayscale-2)">
                {LIMIT_CALLS.HOURLY_LIMIT_REACHED}
                {dayjs(hourlyLimit?.nextCallingTime).isAfter(dayjs(), 'day') ? (
                  <Typography
                    as="span"
                    variant="h5"
                    className="font-medium"
                    color="var(--orange-darkbase)"
                  >
                    {LIMIT_CALLS.TOMORROW}
                  </Typography>
                ) : (
                  <Typography
                    as="span"
                    variant="h5"
                    className="font-medium"
                    color="var(--orange-darkbase)"
                  >
                    {dayjs(hourlyLimit?.nextCallingTime).format('h:mm A')}
                  </Typography>
                )}
              </Typography>
            </div>
          ) : null}
        </div>
        <div>
          <CallHistory list={callData?.callHistory || []} noOfRecords={NO_OF_RECORDS} showTooltip />
        </div>
      </div>
      <div className={styles.sourcesWrapper}>
        {isGlobalRoleAndNnumberformLonghorn ? (
          <GlobalRoleAndNoFromLonghorn callData={callData} />
        ) : (
          isAddedFromLonghorn &&
          (callData?.telephoneCustomerConfidence?.label ? (
            <Tooltip>
              <TooltipTrigger>
                <PersonWithSeperatorIcon />
              </TooltipTrigger>
              <TooltipContent className={styles.sourceTooltipContent}>
                {callData.telephoneCustomerConfidence.label}
              </TooltipContent>
            </Tooltip>
          ) : (
            <PersonWithSeperatorIcon />
          ))
        )}
        <span className={styles.sourceTextNames}>
          {callData?.sources.slice(0, 2).map((source, index) => (
            <>
              <span>
                {PhoneNumberSourceText[source] ?? source}
                {index == 0 && callData?.sources?.length > 1 ? ', ' : ''}
              </span>
            </>
          ))}
        </span>
        {callData?.sources?.length > 2 && (
          <>
            <Tooltip>
              <TooltipTrigger>
                <span className={styles.triggerText}>
                  {' +' + (callData?.sources?.length - 2) + ' more'}
                </span>
              </TooltipTrigger>

              <TooltipContent className={styles.sourceTooltipContent}>
                {callData?.sources?.slice(2, callData?.sources?.length).map((source, index) => (
                  <span key={index}>
                    {PhoneNumberSourceText[source] ?? source}{' '}
                    {index < callData.sources.length - 3 ? ', ' : ''}
                  </span>
                ))}
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  ) : null;
};

export default PhoneNumberContactCard;
